import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { AlertSeverity, CommodityTripwire, ConnectorHealthStatus, TripwireTier } from '../types';

// Data Connectors (signal-feed registry §"Commodities and energy"): the per-commodity breach
// thresholds that decide when an input move trips a signal. Every threshold here is a MODELED
// ESTIMATE on proxy coverage — modeled on the benchmark 99¢-can cost stack, not calibrated to a
// real margin. The screen states that plainly and never presents a threshold as calibrated; the
// numbers are edited in one config file (data/connectors/commodity_tripwires_v1.json), not here.

const SEVERITY_TAG: Record<AlertSeverity, string> = {
  existential: 'red',
  high: 'gold',
  elevated: 'amber',
  watch: 'muted',
};

const HEALTH_TAG: Record<ConnectorHealthStatus, string> = {
  ok: 'olive',
  degraded: 'amber',
  down: 'red',
  not_connected: 'muted',
};

const HEALTH_LABEL: Record<ConnectorHealthStatus, string> = {
  ok: 'connected',
  degraded: 'degraded',
  down: 'down',
  not_connected: 'not connected',
};

export default function Connectors() {
  const conn = useQuery({ queryKey: ['connectors'], queryFn: api.connectors });

  if (conn.isLoading) return <div className="small faint">Loading data connectors…</div>;
  if (conn.isError) return <div className="small faint">Data connectors unavailable.</div>;

  const c = conn.data!;

  return (
    <div>
      <div className="ph">Data Connectors · commodity tripwires</div>
      <div className="h2">The input feeds that trip a signal</div>
      <p className="lead">
        Each commodity feed carries a breach ladder — the % move, against the hedged baseline, that
        trips a signal at each severity. Severity follows money-node exposure, so aluminum can reach
        existential while tea cannot.
      </p>

      {/* Calibration honesty: the whole config is modeled, never calibrated. This banner is the
          standing disclaimer so no threshold below reads as a calibrated figure. */}
      {!c.calibrated && (
        <section className="panel" style={{ borderLeft: '4px solid var(--amber)', marginBottom: 14 }}>
          <div className="qhead">
            <span className="qdot" style={{ background: 'var(--amber)' }} />
            Modeled estimates — not calibrated
            <span className="tag muted" style={{ marginLeft: 8 }}>estimate · {c.coverage}</span>
          </div>
          <p className="small" style={{ marginTop: 6, marginBottom: 0, lineHeight: 1.6 }}>{c.basis}</p>
        </section>
      )}

      <section className="panel">
        <div className="qhead"><span className="qdot" style={{ background: 'var(--blue)' }} />Commodity feeds · {c.commodities.length}</div>
        <table>
          <thead>
            <tr>
              <th>Feed</th>
              <th>Drives</th>
              <th className="center">Cost share</th>
              <th>Breach ladder</th>
              <th className="center">Calibration</th>
              <th className="center">Connection</th>
            </tr>
          </thead>
          <tbody>
            {c.commodities.map((row) => (
              <ConnectorRow key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      </section>

      {/* Honest scope note: these are the numeric % feeds. Other signal families breach on
          different logic, so coverage is not silently capped to commodities. */}
      <section className="panel" style={{ marginTop: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--muted)' }} />Other signal families (not %-threshold feeds)</div>
        <ul className="small" style={{ margin: '6px 0 0', paddingLeft: 18, lineHeight: 1.7 }}>
          {c.other_signal_families.map((fam) => (
            <li key={fam}>{fam}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ConnectorRow({ row }: { row: CommodityTripwire }) {
  const health = row.health?.status ?? 'not_connected';
  return (
    <tr>
      <td>
        <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{row.label}</div>
        <div className="tiny faint">{row.source} · {row.cadence}</div>
        <div className="tiny" style={{ marginTop: 2 }}>
          <span className={`tag ${SEVERITY_TAG[row.severity_ceiling]}`}>ceiling: {row.severity_ceiling}</span>
        </div>
      </td>
      <td className="small mono">{row.drives.join(', ')}</td>
      <td className="center small">{row.cost_share_pct != null ? `~${row.cost_share_pct}%` : '—'}</td>
      <td>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {row.tiers.map((tier) => (
            <TierChip key={`${tier.severity}-${tier.move_pct}`} tier={tier} />
          ))}
        </div>
      </td>
      <td className="center">
        {/* Always reads as a modeled estimate on proxy coverage — never "calibrated". */}
        <span className="tag muted">{row.provenance} · {row.coverage}</span>
      </td>
      <td className="center">
        <span className={`tag ${HEALTH_TAG[health]}`} title={row.health?.detail ?? undefined}>
          {HEALTH_LABEL[health]}
        </span>
      </td>
    </tr>
  );
}

function TierChip({ tier }: { tier: TripwireTier }) {
  return (
    <span
      className={`tag ${SEVERITY_TAG[tier.severity]}`}
      title={`${tier.severity} · ${tier.basis}${tier.note ? ` · ${tier.note}` : ''}`}
    >
      {tier.severity} +{tier.move_pct}%
    </span>
  );
}
